import { useCallback, useEffect, useState } from "react";
import { getMessageTemplatesByOrganizationAsync } from "../api/MessageTemplateApi";
import { MessageTemplate } from "../models/MessageModels";
import { Constants } from "../constants/constants";

interface UseMessageTemplatesReturn {
  templates: MessageTemplate[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  reload: () => Promise<void>;
  addLocal: (template: MessageTemplate) => void;
  updateLocal: (template: MessageTemplate) => void;
  removeLocal: (id: string) => void;
}

export const useMessageTemplates = (
  organizationId: string | undefined,
  lazy = false,
): UseMessageTemplatesReturn => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(!lazy);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
      const res = await getMessageTemplatesByOrganizationAsync(
        token,
        organizationId,
      );
      if (res.success) {
        setTemplates(res.result ?? []);
        setError(null);
      } else {
        setError(res.message || "Failed to load templates");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!lazy) load();
  }, [load, lazy]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const addLocal = useCallback((template: MessageTemplate) => {
    setTemplates((prev) => [template, ...prev]);
  }, []);

  const updateLocal = useCallback((template: MessageTemplate) => {
    setTemplates((prev) =>
      prev.map((item) => (item.id === template.id ? template : item)),
    );
  }, []);

  const removeLocal = useCallback((id: string) => {
    setTemplates((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    templates,
    loading,
    error,
    load,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  };
};